import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../lib/api";
import { Badge, Button, Card, EmptyState, PageHeader, Select, Spinner, statusTone, Table, Tabs, Input, Field } from "../components/ui";
import { SENDING_PROVIDER_LABELS, STATUS_LABELS, cls, fmtDate, fmtPct } from "../lib/format";

type SendingAccount = {
  id: string; name: string; email: string; domain: string; provider: string; smtpHost: string; smtpPort: number;
  smtpUsername: string | null; smtpPasswordEnc: string | null; groupName: string | null; dailySendingLimit: number;
  status: string; monitoringEnabled: boolean; connectionStatus: string; lastCheckedAt: string | null; notes: string | null;
};

type HistoryRow = {
  id: string; reportDate: string; testId: string; testRunId: string; testName: string; testType: string;
  totalSeedAccounts: number; inboxCount: number; categoryCount: number; spamCount: number; missingCount: number;
  inboxPercentage: number; categoryPercentage: number; spamPercentage: number; missingPercentage: number;
  inboxPlacementScore: number; deliverabilityScore: number;
  gmailInboxPercentage: number | null; microsoftInboxPercentage: number | null; yahooInboxPercentage: number | null;
  spfStatus: string | null; dkimStatus: string | null; dmarcStatus: string | null; blacklistCount: number; status: string;
};

type HistoryResponse = {
  account: { id: string; name: string; email: string; provider: string };
  stats: { current: number | null; previous: number | null; avg7: number | null; avg30: number | null; best: number | null; lowest: number | null; totalTests: number };
  history: HistoryRow[];
};

const STATUS_TEXT: Record<string, string> = {
  excellent: "Excellent",
  good: "Good",
  needs_attention: "Needs Attention",
  poor: "Poor",
  critical: "Critical",
};

export function SendingAccountDetail() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "history" ? "history" : "overview");
  const [range, setRange] = useState("30d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [testType, setTestType] = useState("");
  const [provider, setProvider] = useState("");
  const qc = useQueryClient();

  const { data: account, isLoading: accountLoading } = useQuery({
    queryKey: ["sending-account", id],
    queryFn: () => api<SendingAccount>(`/sending-accounts/${id}`),
  });
  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ["sending-history", id, range, from, to, testType, provider],
    queryFn: () =>
      api<HistoryResponse>(
        `/sending-accounts/${id}/report-history?range=${range}${range === "custom" && from ? `&from=${from}` : ""}${range === "custom" && to ? `&to=${to}` : ""}${testType ? `&testType=${testType}` : ""}${provider ? `&provider=${provider}` : ""}`,
      ),
    enabled: Boolean(id),
  });

  const testConnection = useMutation({
    mutationFn: () => api(`/sending-accounts/${id}/test-connection`, { method: "POST", body: "{}" }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ["sending-account", id] });
      alert(r.ok ? `SMTP OK: ${r.message}` : `SMTP failed: ${r.message}`);
    },
  });

  if (accountLoading) return <Spinner label="Loading account…" />;
  if (!account) return <EmptyState title="Account not found" />;

  const chartData = [...(history?.history ?? [])].reverse().map((h) => ({
    date: fmtDate(h.reportDate),
    score: h.inboxPlacementScore,
  }));

  return (
    <div>
      <PageHeader
        title={account.name}
        subtitle={`${account.email} · ${SENDING_PROVIDER_LABELS[account.provider] ?? account.provider}`}
        actions={<Link to="/accounts/sending"><Button variant="secondary">All Sending Accounts</Button></Link>}
      />
      <Tabs
        tabs={[
          { id: "overview", label: "Overview" },
          { id: "history", label: "Report History" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "overview" && (
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Card title="Account details">
            <dl className="space-y-3 text-sm">
              <DetailRow k="Name" v={account.name} />
              <DetailRow k="Email" v={account.email} />
              <DetailRow k="Domain" v={account.domain} />
              <DetailRow k="Provider" v={SENDING_PROVIDER_LABELS[account.provider] ?? account.provider} />
              <DetailRow k="SMTP Server" v={`${account.smtpHost}:${account.smtpPort}`} />
              <DetailRow k="SMTP Username" v={account.smtpUsername ?? "—"} />
              <DetailRow k="Daily Sending Limit" v={`${account.dailySendingLimit}/day`} />
              <DetailRow k="Group" v={account.groupName ?? "—"} />
              <DetailRow k="Notes" v={account.notes ?? "—"} />
            </dl>
          </Card>
          <Card title="Status">
            <div className="space-y-3">
              <div className="flex items-center justify-between"><span className="text-sm text-ink-500">Status</span><Badge tone={account.status === "active" ? "success" : "neutral"}>{STATUS_LABELS[account.status]}</Badge></div>
              <div className="flex items-center justify-between"><span className="text-sm text-ink-500">Connection</span><Badge tone={statusTone(account.connectionStatus)}>{account.connectionStatus}</Badge></div>
              <div className="flex items-center justify-between"><span className="text-sm text-ink-500">Monitoring</span>{account.monitoringEnabled ? <Badge tone="brand">Enabled</Badge> : <Badge tone="neutral">Disabled</Badge>}</div>
              <div className="flex items-center justify-between"><span className="text-sm text-ink-500">Last checked</span><span className="text-sm text-ink-600">{fmtDate(account.lastCheckedAt)}</span></div>
              <div className="pt-2"><Button variant="secondary" onClick={() => testConnection.mutate()} disabled={testConnection.isPending}>{testConnection.isPending ? "Testing…" : "Test SMTP Connection"}</Button></div>
            </div>
          </Card>
        </div>
      )}

      {tab === "history" && (
        <div className="mt-6 space-y-6">
          <Card className="p-0">
            <div className="flex flex-wrap items-center gap-3 border-b border-ink-100 p-4">
              <Select value={range} onChange={(e) => setRange(e.target.value)} className="w-44">
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="custom">Custom Date Range</option>
                <option value="">All Time</option>
              </Select>
              {range === "custom" && (
                <>
                  <Field label=""><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" /></Field>
                  <Field label=""><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" /></Field>
                </>
              )}
              <Select value={testType} onChange={(e) => setTestType(e.target.value)} className="w-44">
                <option value="">All test types</option>
                <option value="one_time">One-time</option>
                <option value="automated">Automated</option>
              </Select>
              <Select value={provider} onChange={(e) => setProvider(e.target.value)} className="w-48">
                <option value="">All providers</option>
                {Object.entries(SENDING_PROVIDER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </div>
          </Card>

          {historyLoading ? (
            <Spinner label="Loading report history…" />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
                <ScoreStat label="Current Score" value={history?.stats.current} />
                <ScoreStat label="Previous Score" value={history?.stats.previous} />
                <ScoreStat label="7-Day Average" value={history?.stats.avg7} />
                <ScoreStat label="30-Day Average" value={history?.stats.avg30} />
                <ScoreStat label="Best Score" value={history?.stats.best} />
                <ScoreStat label="Lowest Score" value={history?.stats.lowest} />
                <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-card">
                  <p className="text-xs font-medium uppercase tracking-wide text-ink-500">Total Tests</p>
                  <p className="mt-2 text-2xl font-bold tabular-nums text-ink-800">{history?.stats.totalTests ?? 0}</p>
                </div>
              </div>

              <Card title="Inbox Placement Score History" subtitle="Inbox placement score per completed test run">
                {chartData.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip formatter={(value: number) => [`${value}/100`, "Inbox Placement Score"]} />
                      <Line type="monotone" dataKey="score" name="Inbox Placement Score" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState title="No report history" description="Completed tests for this sending account will appear here over time." />
                )}
              </Card>

              <Card className="p-0" title="History" subtitle="Permanent per-run records — previous results are never overwritten">
                {!history?.history.length ? (
                  <div className="p-6"><EmptyState title="No records for the selected filters" /></div>
                ) : (
                  <Table headers={["Report Date", "Test Name", "Inbox Score", "Inbox %", "Category %", "Spam %", "Missing %", "Google %", "Microsoft %", "Yahoo %", "Status", ""]}>
                    {history!.history.map((h) => (
                      <tr key={h.id} className="table-row-hover">
                        <td className="whitespace-nowrap px-4 py-3 text-ink-600">{fmtDate(h.reportDate)}</td>
                        <td className="px-4 py-3 font-medium text-ink-800">{h.testName}</td>
                        <td className="px-4 py-3 font-bold tabular-nums text-ink-900">{h.inboxPlacementScore}<span className="text-xs font-normal text-ink-400">/100</span></td>
                        <td className="px-4 py-3 tabular-nums text-emerald-600">{fmtPct(h.inboxPercentage)}</td>
                        <td className="px-4 py-3 tabular-nums text-amber-600">{fmtPct(h.categoryPercentage)}</td>
                        <td className="px-4 py-3 tabular-nums text-red-600">{fmtPct(h.spamPercentage)}</td>
                        <td className="px-4 py-3 tabular-nums text-ink-500">{fmtPct(h.missingPercentage)}</td>
                        <td className="px-4 py-3 tabular-nums text-ink-600">{fmtPct(h.gmailInboxPercentage)}</td>
                        <td className="px-4 py-3 tabular-nums text-ink-600">{fmtPct(h.microsoftInboxPercentage)}</td>
                        <td className="px-4 py-3 tabular-nums text-ink-600">{fmtPct(h.yahooInboxPercentage)}</td>
                        <td className="px-4 py-3"><Badge tone={statusTone(h.status)}>{STATUS_TEXT[h.status] ?? h.status}</Badge></td>
                        <td className="px-4 py-3">
                          <Link to={`/tests/${h.testId}/runs/${h.testRunId}`} className="rounded px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50">
                            View Report
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </Table>
                )}
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DetailRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink-500">{k}</dt>
      <dd className="break-words text-right font-medium text-ink-800">{v}</dd>
    </div>
  );
}

function ScoreStat({ label, value }: { label: string; value: number | null | undefined }) {
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-card">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</p>
      <p className={cls("mt-2 text-2xl font-bold tabular-nums", value !== null && value !== undefined && value >= 80 ? "text-emerald-600" : value !== null && value !== undefined && value >= 70 ? "text-amber-600" : "text-ink-800")}>
        {value ?? "—"}
      </p>
    </div>
  );
}
